import { useState } from 'react';
import { Link } from 'react-router-dom';

type BillingCycle = 'monthly' | 'yearly';

interface Plan {
  id: string;
  name: string;
  badge?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  description: string;
  color: string;
  glowColor: string;
  borderColor: string;
  btnClass: string;
  features: { text: string; available: boolean }[];
  highlighted: boolean;
}

const plans: Plan[] = [
  {
    id: 'free',
    name: 'Ücretsiz',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: 'Temel sohbet deneyimi, herkes için.',
    color: 'text-white',
    glowColor: '',
    borderColor: 'border-white/10',
    btnClass: 'bg-white/8 border border-white/15 text-white hover:bg-white/12',
    highlighted: false,
    features: [
      { text: 'Rastgele video sohbet', available: true },
      { text: 'Metin sohbeti', available: true },
      { text: 'Temel bağlantı kalitesi', available: true },
      { text: 'Mobil uyumluluk', available: true },
      { text: 'Reklamsız deneyim', available: false },
      { text: 'Cinsiyet filtresi', available: false },
      { text: 'Ülke filtresi', available: false },
      { text: 'Öncelikli eşleşme', available: false },
      { text: 'Yeniden bağlan', available: false },
      { text: 'HD video kalitesi', available: false },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    badge: 'EN POPÜLER',
    monthlyPrice: 99,
    yearlyPrice: 69,
    description: 'Tam kontrol, sıfır kısıtlama.',
    color: 'text-yellow-400',
    glowColor: 'bg-yellow-500/10',
    borderColor: 'border-yellow-500/40',
    btnClass: 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-extrabold hover:from-yellow-400 hover:to-orange-400 shadow-lg shadow-yellow-500/25',
    highlighted: true,
    features: [
      { text: 'Rastgele video sohbet', available: true },
      { text: 'Metin sohbeti', available: true },
      { text: 'HD video kalitesi', available: true },
      { text: 'Mobil uyumluluk', available: true },
      { text: 'Reklamsız deneyim', available: true },
      { text: 'Cinsiyet filtresi', available: true },
      { text: 'Ülke filtresi', available: true },
      { text: 'Öncelikli eşleşme', available: true },
      { text: 'Yeniden bağlan', available: true },
      { text: 'Özel profil rozeti', available: false },
    ],
  },
  {
    id: 'premium-plus',
    name: 'Premium+',
    badge: 'YENİ',
    monthlyPrice: 179,
    yearlyPrice: 129,
    description: 'Elit kullanıcılar için tam paket.',
    color: 'text-cyan-400',
    glowColor: 'bg-cyan-500/8',
    borderColor: 'border-cyan-500/30',
    btnClass: 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white font-extrabold hover:from-cyan-400 hover:to-violet-500 shadow-lg shadow-cyan-500/20',
    highlighted: false,
    features: [
      { text: 'Premium\'un tüm özellikleri', available: true },
      { text: 'HD video kalitesi', available: true },
      { text: 'Özel profil rozeti', available: true },
      { text: 'Çoklu filtre kombinasyonu', available: true },
      { text: 'Reklamsız deneyim', available: true },
      { text: 'Cinsiyet filtresi', available: true },
      { text: 'Ülke filtresi', available: true },
      { text: 'Öncelikli eşleşme', available: true },
      { text: 'Yeniden bağlan', available: true },
      { text: 'Özel profil rozeti', available: true },
    ],
  },
];

const PricingCards = () => {
  const [billing, setBilling] = useState<BillingCycle>('monthly');

  return (
    <section className="py-16 md:py-20 bg-[#05070d]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Billing toggle */}
        <div className="flex flex-col items-center mb-14">
          <div className="flex items-center gap-1 bg-white/5 border border-white/10 rounded-full p-1 mb-3">
            <button
              onClick={() => setBilling('monthly')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                billing === 'monthly'
                  ? 'bg-white/12 text-white'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              Aylık
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                billing === 'yearly'
                  ? 'bg-white/12 text-white'
                  : 'text-white/40 hover:text-white/70'
              }`}
            >
              Yıllık
            </button>
          </div>
          {billing === 'yearly' && (
            <span className="text-emerald-400 text-xs font-semibold px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25">
              Yıllık ödemede %30 tasarruf et!
            </span>
          )}
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          {plans.map((plan) => {
            const price = billing === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-3xl border p-8 transition-all duration-300 ${
                  plan.highlighted
                    ? `${plan.glowColor} ${plan.borderColor} scale-[1.03]`
                    : `bg-white/3 ${plan.borderColor}`
                }`}
              >
                {/* Glow effect for highlighted */}
                {plan.highlighted && (
                  <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-yellow-500/20 via-transparent to-orange-500/10 pointer-events-none" />
                )}

                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span
                      className={`px-4 py-1.5 rounded-full text-xs font-extrabold whitespace-nowrap ${
                        plan.id === 'premium'
                          ? 'bg-gradient-to-r from-yellow-500 to-orange-500 text-black'
                          : 'bg-gradient-to-r from-cyan-500 to-violet-600 text-white'
                      }`}
                    >
                      {plan.badge}
                    </span>
                  </div>
                )}

                {/* Plan name */}
                <div className="flex items-center gap-2 mb-2">
                  <i
                    className={`text-lg ${
                      plan.id === 'free'
                        ? 'ri-user-line text-white/40'
                        : plan.id === 'premium'
                        ? 'ri-vip-crown-line text-yellow-400'
                        : 'ri-vip-diamond-line text-cyan-400'
                    }`}
                  />
                  <span className={`font-extrabold text-lg ${plan.color}`}>{plan.name}</span>
                </div>
                <p className="text-white/40 text-sm mb-6">{plan.description}</p>

                {/* Price */}
                <div className="mb-8">
                  <div className="flex items-end gap-1">
                    <span className="text-5xl font-extrabold text-white">
                      {price === 0 ? 'Ücretsiz' : `₺${price}`}
                    </span>
                    {price > 0 && (
                      <span className="text-white/35 text-sm mb-2">/ay</span>
                    )}
                  </div>
                  {billing === 'yearly' && price > 0 && (
                    <p className="text-white/30 text-xs mt-1">
                      Yıllık ₺{price * 12} faturalandırılır
                    </p>
                  )}
                  {billing === 'monthly' && plan.id !== 'free' && (
                    <p className="text-emerald-400 text-xs mt-1">
                      Yıllık planla ₺{plan.monthlyPrice - plan.yearlyPrice}/ay tasarruf
                    </p>
                  )}
                </div>

                {/* CTA */}
                <Link
                  to={plan.id === 'free' ? '/auth' : '/auth?mode=register'}
                  className={`w-full py-3.5 rounded-full text-sm text-center transition-all duration-200 cursor-pointer whitespace-nowrap mb-8 ${plan.btnClass}`}
                >
                  {plan.id === 'free' ? 'Ücretsiz Başla' : `${plan.name}\'e Geç`}
                </Link>

                {/* Divider */}
                <div className="h-px bg-white/8 mb-6" />

                {/* Features */}
                <ul className="flex flex-col gap-3 flex-1">
                  {plan.features.map((feat) => (
                    <li key={feat.text} className="flex items-center gap-3">
                      <div
                        className={`w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0 ${
                          feat.available
                            ? plan.id === 'premium'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : plan.id === 'premium-plus'
                              ? 'bg-cyan-500/15 text-cyan-400'
                              : 'bg-white/8 text-white/40'
                            : 'bg-white/4 text-white/15'
                        }`}
                      >
                        <i
                          className={`text-xs ${
                            feat.available ? 'ri-check-line' : 'ri-close-line'
                          }`}
                        />
                      </div>
                      <span
                        className={`text-sm ${
                          feat.available ? 'text-white/70' : 'text-white/20 line-through'
                        }`}
                      >
                        {feat.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Note */}
        <p className="text-center text-white/25 text-xs mt-8">
          İstediğin zaman iptal et · Kredi kartı güvenliği · 7 gün para iade garantisi
        </p>
      </div>
    </section>
  );
};

export default PricingCards;
